import { useCallback, useState } from 'react'

/**
 * Whether the Properties Panel is showing: `isExpanded` (inline, desktop/tablet) or `mobileOpen`
 * (floating, mobile). The rail itself is always visible.
 */
export function useLeftPanelVisibility(isMobile: boolean) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  /** Shows the Properties Panel in whichever form the current viewport uses. */
  const revealPanel = useCallback(() => {
    if (isMobile) {
      setMobileOpen(true)
    } else {
      setIsExpanded(true)
    }
  }, [isMobile])

  /** Hides the Properties Panel in whichever form the current viewport uses. */
  const hidePanel = useCallback(() => {
    if (isMobile) {
      setMobileOpen(false)
    } else {
      setIsExpanded(false)
    }
  }, [isMobile])

  /**
   * Flips the panel open/closed; `onCollapse` runs when it closes. Called before the state update
   * rather than inside its updater: React can run an updater while rendering this panel, and
   * `onCollapse` sets the parent's state (e.g. resetting the Shapes tool), which React rejects
   * during another component's render.
   */
  const togglePanel = useCallback(
    (onCollapse: () => void) => {
      const isOpen = isMobile ? mobileOpen : isExpanded
      const setOpen = isMobile ? setMobileOpen : setIsExpanded

      if (isOpen) {
        onCollapse()
      }

      setOpen(!isOpen)
    },
    [isMobile, isExpanded, mobileOpen]
  )

  return {
    isExpanded,
    setIsExpanded,
    mobileOpen,
    setMobileOpen,
    revealPanel,
    hidePanel,
    togglePanel,
  }
}
